// ─────────────────────────────────────────────────────────────────────────────
// CrewZ BLE Service (Web port)
// Mirrors src/services/crewz-ble.ts in jas0nday/Crew-Z (React Native).
//
// On web we use the Web Bluetooth API (navigator.bluetooth). The service is
// shipped as a singleton scaffolded against the published Crew-Z GATT profile.
// Real CrewZ hardware is forthcoming – until then, scanForDevices() stays a
// no-op so the existing PM5 flow is unaffected. The interface (events, types,
// methods) intentionally matches the React Native counterpart.
// ─────────────────────────────────────────────────────────────────────────────

import { CREWZ_UUIDS, CREWZ_SCAN_UUIDS } from '../config/bleUuids';

/**
 * @typedef {Object} RowingMetrics
 * @property {number} speed        m/s
 * @property {number} split        seconds per 500m
 * @property {number} strokeRate   SPM
 * @property {number} strokeCount  cumulative
 * @property {number} distance     metres
 * @property {number} elapsedTime  seconds
 * @property {number} heartRate    BPM (0 = no belt)
 */

/**
 * @typedef {Object} DeviceStatus
 * @property {number} battery
 * @property {0|1|2} gpsFix
 * @property {number} bleConnCount
 * @property {number} variant
 * @property {string} firmwareVersion
 */

/**
 * @typedef {Object} CrewZDevice
 * @property {string} id
 * @property {string} name
 * @property {number} rssi
 * @property {number=} variant
 */

class SimpleEventEmitter {
  constructor() {
    this._listeners = new Map();
  }
  on(event, handler) {
    const arr = this._listeners.get(event) || [];
    arr.push(handler);
    this._listeners.set(event, arr);
  }
  off(event, handler) {
    const arr = this._listeners.get(event) || [];
    this._listeners.set(event, arr.filter(h => h !== handler));
  }
  emit(event, ...args) {
    const arr = this._listeners.get(event) || [];
    arr.forEach(h => {
      try { h(...args); } catch (e) { console.error(`[CrewZBLE] listener error on '${event}':`, e); }
    });
  }
}

class CrewZBleService extends SimpleEventEmitter {
  constructor() {
    super();
    this.connectionState = 'idle'; // idle|scanning|connecting|connected|disconnected|error
    this.connectedDevice = null;
    this.device = null;
    this.server = null;
    this.metrics = null;
    console.log('[CrewZBLE] Service initialised (Web Bluetooth – awaiting CrewZ hardware)');
  }

  /** Web Bluetooth requires a user-gesture for scan. Returns the chosen device. */
  async scanForDevices() {
    if (typeof navigator === 'undefined' || !navigator.bluetooth) {
      console.warn('[CrewZBLE] Web Bluetooth not supported in this browser');
      return [];
    }
    this.connectionState = 'scanning';
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: CREWZ_SCAN_UUIDS }],
        optionalServices: [
          CREWZ_UUIDS.DEVICE_STATUS_SERVICE,
          CREWZ_UUIDS.CONFIGURATION_SERVICE,
          CREWZ_UUIDS.COACH_METRICS_SERVICE,
        ],
      });
      const result = {
        id: device.id,
        name: device.name || 'CrewZ',
        rssi: 0,
      };
      this.device = device;
      return [result];
    } catch (err) {
      this.connectionState = 'idle';
      console.warn('[CrewZBLE] Scan cancelled or failed:', err?.message || err);
      return [];
    }
  }

  async connect(/* deviceId */) {
    if (!this.device) {
      console.warn('[CrewZBLE] connect() called without scanForDevices()');
      return false;
    }
    this.connectionState = 'connecting';
    try {
      this.server = await this.device.gatt.connect();
      this.connectedDevice = {
        id: this.device.id,
        name: this.device.name || 'CrewZ',
        rssi: 0,
      };
      this.connectionState = 'connected';
      this.device.addEventListener('gattserverdisconnected', () => this._handleDisconnect());
      this.emit('connected', this.connectedDevice);
      // Subscribing to notifications would happen here once firmware is final.
      return true;
    } catch (err) {
      this.connectionState = 'error';
      console.error('[CrewZBLE] connect failed:', err);
      return false;
    }
  }

  async disconnect() {
    try {
      if (this.device?.gatt?.connected) this.device.gatt.disconnect();
    } catch (err) {
      console.error('[CrewZBLE] disconnect error:', err);
    }
    this._handleDisconnect();
  }

  _handleDisconnect() {
    this.connectedDevice = null;
    this.server = null;
    this.metrics = null;
    this.connectionState = 'disconnected';
    this.emit('disconnected');
  }

  getConnectionState() { return this.connectionState; }
  getConnectedDevice() { return this.connectedDevice; }
  isConnected() { return this.connectionState === 'connected'; }
}

export const crewzBleService = new CrewZBleService();
